import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Shield, ArrowLeft, Loader2, Save, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabaseClient';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    phone: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Initialize form with user data
    if (user) {
      setFormData({
        fullName: user.user_metadata?.full_name || user.user_metadata?.name || '',
        phone: user.user_metadata?.phone || user.phone || ''
      });
    }
  }, [isAuthenticated, navigate, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: formData.fullName,
          phone: formData.phone
        }
      });

      if (error) {
        toast({
          title: "Update Failed",
          description: error.message,
          variant: "destructive"
        });
        return;
      }

      // Update localStorage for compatibility
      if (formData.fullName) {
        localStorage.setItem('userName', formData.fullName);
      }

      setSaved(true);
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });

      // Reset saved indicator after 3 seconds
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast({
        title: "Update Failed",
        description: "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }} data-testid="profile-page">
      <section className="section-padding">
        <div className="container max-w-4xl">
          {/* Back Link */}
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 mb-8 body-small hover:opacity-70 transition-opacity"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>

          {/* Header */}
          <div className="mb-12">
            <h1 className="hero-medium mb-2">My Profile</h1>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Manage your personal information and preferences
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Summary Card */}
            <div 
              className="lg:col-span-1 p-8"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-light)'
              }}
            >
              {/* Avatar */}
              <div className="flex flex-col items-center text-center mb-8">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center mb-4"
                  style={{ 
                    backgroundColor: 'var(--olive-primary)',
                    color: 'var(--bg-primary)'
                  }}
                >
                  {user?.user_metadata?.avatar_url ? (
                    <img 
                      src={user.user_metadata.avatar_url} 
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover"
                    />
                  ) : (
                    <User size={40} />
                  )}
                </div>
                <h2 className="heading-3 mb-1">
                  {formData.fullName || user?.email?.split('@')[0] || 'Guest'}
                </h2>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {user?.email}
                </p>
              </div>

              {/* Account Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar size={18} style={{ color: 'var(--text-secondary)' }} />
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Member Since</p>
                    <p className="body-regular">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Shield size={18} style={{ color: 'var(--text-secondary)' }} />
                  <div>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Account Type</p>
                    <p className="body-regular">
                      {user?.app_metadata?.provider === 'google' ? 'Google Account' : 'Email Account'}
                    </p>
                  </div>
                </div>

                {user?.email_confirmed_at && (
                  <div className="flex items-center gap-2 mt-4 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
                    <Check size={16} style={{ color: 'var(--status-success)' }} />
                    <span className="body-small" style={{ color: 'var(--status-success)' }}>
                      Email Verified
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Edit Profile Form */}
            <div 
              className="lg:col-span-2 p-8"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-light)'
              }}
            >
              <h2 className="heading-3 mb-6">Edit Profile</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email (Read-only) */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail 
                      size={18} 
                      className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--text-light)' }}
                    />
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="pl-10"
                      style={{ 
                        borderRadius: 0,
                        backgroundColor: 'var(--bg-primary)',
                        borderColor: 'var(--border-light)',
                        color: 'var(--text-secondary)',
                        opacity: 0.7
                      }}
                    />
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-light)' }}>
                    Email cannot be changed for security reasons
                  </p>
                </div>

                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Full Name
                  </Label>
                  <div className="relative">
                    <User 
                      size={18} 
                      className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--text-light)' }}
                    />
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="pl-10"
                      style={{ 
                        borderRadius: 0,
                        backgroundColor: 'var(--bg-primary)',
                        borderColor: 'var(--border-light)'
                      }}
                      data-testid="fullname-input"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Phone Number
                  </Label>
                  <div className="relative">
                    <Phone 
                      size={18} 
                      className="absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'var(--text-light)' }}
                    />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+263 77 123 4567"
                      className="pl-10"
                      style={{ 
                        borderRadius: 0,
                        backgroundColor: 'var(--bg-primary)',
                        borderColor: 'var(--border-light)'
                      }}
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="btn-primary w-full md:w-auto flex items-center justify-center gap-2"
                    data-testid="save-profile-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Saving...
                      </>
                    ) : saved ? (
                      <>
                        <Check size={18} />
                        Saved!
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Quick Links */}
          <div 
            className="mt-8 p-8"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)'
            }}
          >
            <h3 className="heading-3 mb-6">Quick Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/dashboard">
                <div 
                  className="p-4 text-center hover-lift cursor-pointer"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <p className="body-regular">My Bookings</p>
                </div>
              </Link>
              <Link to="/booking">
                <div 
                  className="p-4 text-center hover-lift cursor-pointer"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <p className="body-regular">Make a Reservation</p>
                </div>
              </Link>
              <Link to="/rooms">
                <div 
                  className="p-4 text-center hover-lift cursor-pointer"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <p className="body-regular">Explore Rooms</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;
